// Bottom tab nav — 5 tabs with a CSS-only "Golden Aura" active indicator.
// 2026-05-04 PERF — removed framer-motion import (saved ~67KB brotli / ~2s on
// Slow-4G from critical path — this component is eager-loaded by Navbar on
// /match-centre which is the root route for 99% of users). The subtle cross-
// fade of the aura between tabs still feels premium via pure CSS opacity.
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Trophy, Wallet, Gift, User, Sparkles } from 'lucide-react';
import { useProStatus } from '../../hooks/useProStatus';
import { useFestival } from '../../hooks/useFestival';

const TABS = [
  { to: '/v2/home',   label: 'Home',    Icon: Home,      testid: 'f11v2-tab-home' },
  { to: '/v2/play',   label: 'Play',    Icon: Trophy,    testid: 'f11v2-tab-play' },
  { to: '/earn',      label: 'Earn',    Icon: Sparkles,  testid: 'f11v2-tab-earn' },
  { to: '/v2/wallet', label: 'Wallet',  Icon: Wallet,    testid: 'f11v2-tab-wallet' },
  { to: '/v2/shop',   label: 'Get',     Icon: Gift,      testid: 'f11v2-tab-shop' },
  { to: '/profile',   label: 'You',     Icon: User,      testid: 'f11v2-tab-profile' },
];

// Consider common aliases for the Home tab: V2 default flow lands at /dashboard
// or / (V2MatchCentre falls back to /v2/play). We match on the prefix of the
// tab route so /contest-hub/:id keeps the "Play" tab highlighted, etc.
function isTabActive(pathname, to) {
  if (to === '/v2/home')   return ['/v2/home', '/dashboard'].some(p => pathname === p);
  if (to === '/v2/play')   return pathname.startsWith('/v2/play') || pathname === '/' || pathname.startsWith('/match-centre') || pathname.startsWith('/contest-hub');
  if (to === '/earn')      return pathname.startsWith('/earn') && !pathname.startsWith('/earnings'); // exact /earn + sub
  if (to === '/v2/wallet') return pathname.startsWith('/v2/wallet') || pathname.startsWith('/wallet');
  if (to === '/v2/shop')   return pathname.startsWith('/v2/shop')   || pathname.startsWith('/shop');
  if (to === '/profile')   return pathname.startsWith('/profile');
  return pathname === to;
}

export default function V2BottomNav() {
  const { pathname } = useLocation();
  const { isPro } = useProStatus();
  const fest = useFestival();
  const festActive = fest?.phase === 'teaser' || fest?.phase === 'live';

  return (
    <>
    <style>{`
      @keyframes f11v2-pro-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.55; transform: scale(1.35); }
      }
    `}</style>
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 flex justify-around items-center border-t px-1"
      style={{
        background: 'var(--f11v2-bar-bg)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderColor: 'var(--f11v2-border)',
        paddingTop: '0.5rem',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0) + 0.5rem)',
        gap: 2,
      }}
      data-testid="f11v2-bottom-nav"
      role="navigation"
    >
      {TABS.map(({ to, label, Icon, testid }) => {
        const active = isTabActive(pathname, to);
        return (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className="relative flex flex-col items-center gap-1 py-1.5 px-1 rounded-xl transition-transform flex-1"
            style={{
              color: active ? 'var(--f11v2-gold)' : 'var(--f11v2-text-mute)',
              transform: active ? 'scale(1.05)' : 'scale(1)',
              opacity: active ? 1 : 0.7,
              minWidth: 0,
              maxWidth: 72,
            }}
          >
            {active && (
              <span
                data-testid={`${testid}-active`}
                className="absolute inset-0 rounded-xl pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.00) 90%)',
                  borderTop: '1px solid rgba(212,175,55,0.9)',
                  boxShadow: '0 -2px 14px rgba(212,175,55,0.35)',
                  animation: 'f11v2-aura-in 220ms ease-out',
                }}
              />
            )}
            <Icon size={18} strokeWidth={2.2} style={{ position: 'relative', zIndex: 1 }} />
            {/* Rakhi marker on the Get tab — carries the event into the nav itself
                so the promotion is present on every screen, not just the home page. */}
            {to === '/v2/shop' && festActive && (
              <span
                data-testid="f11v2-tab-shop-rakhi-badge"
                style={{
                  position: 'absolute', top: 1, right: 3,
                  padding: '1px 4px', borderRadius: 6,
                  fontSize: 7, fontWeight: 900, letterSpacing: '0.04em',
                  background: '#C2185B', color: '#fff',
                  boxShadow: '0 0 8px rgba(194,24,91,0.7)',
                  animation: 'f11v2-pro-pulse 2s ease-in-out infinite',
                  zIndex: 2,
                }}
              >
                RAKHI
              </span>
            )}
            {/* PRO badge — pulsing crown dot on Wallet tab for non-pro users */}
            {to === '/v2/wallet' && !isPro && (
              <span
                data-testid="f11v2-tab-wallet-pro-badge"
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 6,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--f11v2-gold)',
                  boxShadow: '0 0 6px #C6A052',
                  animation: 'f11v2-pro-pulse 2s ease-in-out infinite',
                  zIndex: 2,
                }}
              />
            )}
            <span
              className="f11v2-digits"
              style={{
                fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.02em',
                position: 'relative', zIndex: 1,
                maxWidth: '100%', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
          </NavLink>
        );
      })}
    </nav>
    </>
  );
}
